import { Link } from 'react-router-dom';

import { LegalPage, P, Section, UL } from './LegalPage';

export function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      subtitle="How Emailforge collects, uses, and protects information about workspaces, members, and email recipients."
      effectiveDate="April 25, 2026"
    >
      <Section title="Who we are">
        <P>
          Emailforge is operated by Contra Products. When this policy says "we", "us", or "Emailforge", it refers to
          Contra Products acting as data controller for workspace/member account data, and as data processor for
          email content and recipient data your workspace sends through us.
        </P>
      </Section>

      <Section title="What we collect">
        <UL>
          <li>
            <strong>Account data</strong> — name, email, password hash (argon2), OAuth identities you connect (Google,
            GitHub, Discord), workspace memberships, and the IP address of each session.
          </li>
          <li>
            <strong>Workspace content</strong> — templates (AST + JSX), brand settings, sender identities, integration
            configurations, and feature-flag overrides you set.
          </li>
          <li>
            <strong>Send metadata</strong> — for every email your workspace sends: a one-way hash of the recipient
            address, template ID, provider used, latency, status, and engagement events (delivered/opened/clicked/
            bounced) where the recipient interacts with our tracking pixel or links.
          </li>
          <li>
            <strong>Provider credentials</strong> — SMTP/API keys you connect are encrypted at rest with AES-256-GCM
            using a per-tenant data key. We do not log decrypted credentials.
          </li>
          <li>
            <strong>Operational telemetry</strong> — request IDs, error stacks, and aggregate timings used to keep
            the service healthy.
          </li>
        </UL>
      </Section>

      <Section title="What we don't store">
        <UL>
          <li>Plaintext recipient email addresses for analytics — we hash them with HMAC-SHA256 before persisting.</li>
          <li>Raw email bodies after delivery, beyond a 7-day audit retention window.</li>
          <li>Cookies for marketing or third-party advertising. The marketing site uses no third-party trackers.</li>
        </UL>
      </Section>

      <Section title="How we use it">
        <UL>
          <li>To run the product: render templates, queue and dispatch sends, surface analytics in your dashboard.</li>
          <li>To detect abuse and enforce sending limits and deliverability requirements.</li>
          <li>To communicate with workspace owners about billing, security incidents, and material policy changes.</li>
        </UL>
      </Section>

      <Section title="Subprocessors">
        <P>We use a short list of vetted infrastructure subprocessors:</P>
        <UL>
          <li>The cloud provider you select for compute and Postgres storage.</li>
          <li>Stripe for billing.</li>
          <li>Whichever email provider(s) your workspace explicitly connects (Resend, SendGrid, Postmark, etc.).</li>
          <li>n8n for advanced automation execution, when enabled.</li>
        </UL>
        <P>
          See the <Link to="/dpa" style={{ color: 'var(--flame-2)' }}>Data Processing Agreement</Link> for the
          authoritative subprocessor list and how we notify of changes.
        </P>
      </Section>

      <Section title="Your rights">
        <P>You can, at any time:</P>
        <UL>
          <li>Export every byte we hold about you under <span className="mono">Settings → Danger zone → Export my data</span>.</li>
          <li>Delete your account; we anonymize personal identifiers and keep only what we need for compliance and accounting.</li>
          <li>Request deletion of a specific recipient's engagement records — contact us within 30 days of a request.</li>
        </UL>
      </Section>

      <Section title="Retention">
        <UL>
          <li>Account data — until you delete the account.</li>
          <li>Send events — 13 months by default. Workspace owners can shorten this.</li>
          <li>Audit log — 24 months (compliance and incident response).</li>
        </UL>
      </Section>

      <Section title="Contact">
        <P>
          Privacy questions: <a href="mailto:privacy@emailforge.app" style={{ color: 'var(--flame-2)' }}>privacy@emailforge.app</a>.
          For data subject requests under GDPR/CCPA, see the{' '}
          <Link to="/contact" style={{ color: 'var(--flame-2)' }}>contact page</Link>.
        </P>
      </Section>
    </LegalPage>
  );
}
